import * as sdk from 'matrix-js-sdk'
import { EventType } from 'matrix-js-sdk'
import type { MatrixClient, Room } from 'matrix-js-sdk'
import type { BridgeType } from '../store/room-store'

type DirectContent = Record<string, string[]>

const BRIDGE_PATTERNS: [RegExp, BridgeType][] = [
  [/^@whatsapp[_.]/, 'whatsapp'],
  [/^@telegram[_.]/, 'telegram'],
  [/^@signal[_.]/, 'signal'],
  [/^@discord[_.]/, 'discord'],
  [/^@slack[_.]/, 'slack'],
]

// The bridge's control/management user. Matches the common naming variants:
// @whatsapp:, @whatsappbot:, @whatsapp_bot: (and the same for other networks).
// This must be checked *before* the ghost test, because the underscore variant
// (@whatsapp_bot) also matches the contact-ghost pattern (@whatsapp_<id>) and
// would otherwise be miscounted as a real contact.
const BRIDGE_BOT_RE = /^@(whatsapp|telegram|signal|discord|slack)([_.]?bot)?:/

export function isBridgeBot(userId: string): boolean {
  return BRIDGE_BOT_RE.test(userId)
}

function isBridgeGhost(userId: string): boolean {
  return !isBridgeBot(userId) && BRIDGE_PATTERNS.some(([pattern]) => pattern.test(userId))
}

/** Detect whether a room is bridged from another network, by its members. */
export function detectBridge(room: Room): BridgeType {
  for (const member of room.getJoinedMembers()) {
    for (const [pattern, type] of BRIDGE_PATTERNS) {
      if (pattern.test(member.userId)) return type
    }
  }
  return null
}

/**
 * Custom account data tracking rooms the user has explicitly moved *out* of
 * Direct. m.direct only expresses the positive case, so we need this to let a
 * manual override beat the auto heuristic.
 */
export const NOT_DIRECT_EVENT = 'io.chatpulse.not_direct'

function getDirectContent(client: MatrixClient): DirectContent {
  return (client.getAccountData(EventType.Direct)?.getContent() as DirectContent | undefined) ?? {}
}

function getNotDirectRoomIds(client: MatrixClient): Set<string> {
  const content = client
    .getAccountData(NOT_DIRECT_EVENT as keyof sdk.AccountDataEvents)
    ?.getContent() as { rooms?: string[] } | undefined
  return new Set(content?.rooms ?? [])
}

/** Find the canonical DM partner from m.direct account data, if any. */
function canonicalDirectUserId(client: MatrixClient, roomId: string): string | null {
  const me = client.getUserId()
  let selfKeyed = false
  for (const [userId, roomIds] of Object.entries(getDirectContent(client))) {
    if (!Array.isArray(roomIds) || !roomIds.includes(roomId)) continue
    // A real other-user mapping always wins.
    if (userId !== me) return userId
    // m.direct can get polluted with a *self* key (Room.guessDMUserId() returns
    // your own id when it can't find the other member, and that can be written
    // into m.direct). Don't treat yourself as the partner — that's what put your
    // own avatar on these DMs — but remember it still marks the room as a DM.
    selfKeyed = true
  }
  if (!selfKeyed) return null
  // It is a DM, just mis-keyed: resolve the real partner from the membership.
  const room = client.getRoom(roomId)
  const other = room
    ?.getMembers()
    .find((m) => (m.membership === 'join' || m.membership === 'invite') && m.userId !== me)
  return other?.userId ?? null
}

interface RoomParties {
  /** Remote contacts (e.g. @whatsapp_<id>), excluding the bridge bot. */
  ghosts: string[]
  /** Native Matrix users other than you, excluding bridge bots/ghosts. */
  humans: string[]
  /** Bridge management bots (e.g. @whatsappbot). */
  bots: string[]
}

/** Classify a room's members (excluding you) into contacts, humans and bots. */
function roomParties(client: MatrixClient, room: Room): RoomParties {
  const me = client.getUserId()
  const others = room
    .getMembers()
    .filter((m) => (m.membership === 'join' || m.membership === 'invite') && m.userId !== me)
    .map((m) => m.userId)

  return {
    bots: others.filter(isBridgeBot),
    ghosts: others.filter(isBridgeGhost),
    humans: others.filter((id) => !isBridgeBot(id) && !isBridgeGhost(id)),
  }
}

/** A room whose only other occupants are bridge bots (the management room). */
function isBotManagementRoom({ ghosts, humans, bots }: RoomParties): boolean {
  return bots.length > 0 && ghosts.length === 0 && humans.length === 0
}

/**
 * Choose which ghost represents the *contact* in a bridged 1:1. Normally there is
 * just one. Under double-puppeting your own number also joins as a ghost, and the
 * bridge mirrors your Matrix profile onto it (same avatar / display name) — so it
 * looks like you. Picking that one would show *your* avatar on the DM in the room
 * list, so prefer the ghost that doesn't look like you.
 */
function pickContactGhost(client: MatrixClient, room: Room, ghosts: string[]): string {
  if (ghosts.length === 1) return ghosts[0]!

  // mautrix names a 1:1 portal after the contact, so the ghost whose display name
  // matches the room name is the contact — not your own double-puppet ghost, which
  // carries *your* Signal name/avatar (and thus put your photo on these DMs).
  const byRoomName = ghosts.find((id) => {
    const name = room.getMember(id)?.name
    return !!name && name === room.name
  })
  if (byRoomName) return byRoomName

  // Fallback: drop any ghost that looks like your own Matrix account.
  const me = client.getUserId()
  const myself = me ? room.getMember(me) : null
  const myAvatar = myself?.getMxcAvatarUrl()
  const myName = myself?.name
  const contacts = ghosts.filter((id) => {
    const member = room.getMember(id)
    const looksLikeMe =
      (!!myAvatar && member?.getMxcAvatarUrl() === myAvatar) ||
      (!!myName && member?.name === myName)
    return !looksLikeMe
  })
  return contacts[0] ?? ghosts[0]!
}

/**
 * Best-effort guess at the other party of a one-to-one conversation:
 *   - bridged 1:1  → one or two contact ghosts and no extra humans. (Two arises
 *     under double-puppeting, where your own number is also a ghost.) Bridged
 *     groups have more contacts, so they stay out of Direct.
 *   - native DM    → exactly one other human and no explicit room name (named
 *     two-person rooms like "NotifyNYC Alerts" stay as group rooms).
 */
function heuristicDirectUserId(client: MatrixClient, room: Room, { ghosts, humans }: RoomParties): string | null {
  if (humans.length === 0 && ghosts.length >= 1 && ghosts.length <= 2)
    return pickContactGhost(client, room, ghosts)

  if (ghosts.length === 0 && humans.length === 1) {
    const nameEvent = room.currentState.getStateEvents('m.room.name', '')
    const hasExplicitName = !!(nameEvent?.getContent() as { name?: string } | undefined)?.name
    if (!hasExplicitName) return humans[0]!
  }
  return null
}

/**
 * Resolve the other party of a direct message room. Precedence:
 *   1. explicit "not direct" override   → never a DM
 *   2. bridge bot management room        → never a DM (beats m.direct, which the
 *      bridge auto-populates with its own control room)
 *   3. m.direct account data             → DM (manual mark / other clients)
 *   4. heuristic (native or bridged 1:1) → DM
 *
 * We avoid Room.guessDMUserId() as the primary signal because it treats every
 * two-person room as a DM, flooding the list with bridge bot rooms.
 */
export function getDirectUserId(client: MatrixClient, room: Room): string | null {
  if (getNotDirectRoomIds(client).has(room.roomId)) return null

  const parties = roomParties(client, room)
  if (isBotManagementRoom(parties)) return null

  const canonical = canonicalDirectUserId(client, room.roomId)
  const resolved = canonical ?? heuristicDirectUserId(client, room, parties)
  // Never treat ourselves as the other party (would show our own avatar/name).
  return resolved === client.getUserId() ? null : resolved
}

export function isDirectRoom(client: MatrixClient, room: Room): boolean {
  return getDirectUserId(client, room) !== null
}

/**
 * Find the management room for a bridge network (the room whose only other
 * member is that network's control bot, e.g. @whatsappbot). This is where bridge
 * commands like "start-chat" are sent.
 */
export function getBridgeBotRoom(client: MatrixClient, network: NonNullable<BridgeType>): string | null {
  const me = client.getUserId()
  for (const room of client.getRooms()) {
    if (room.getMyMembership() !== 'join') continue
    const others = room
      .getMembers()
      .filter((m) => (m.membership === 'join' || m.membership === 'invite') && m.userId !== me)
    if (others.length === 1) {
      const id = others[0]!.userId
      if (isBridgeBot(id) && id.startsWith(`@${network}`)) return room.roomId
    }
  }
  return null
}

/** Identify the other occupant of a room so it can be tagged in m.direct. */
function otherMemberId(client: MatrixClient, room: Room): string | null {
  const me = client.getUserId()
  // guessDMUserId() falls back to *our own* id when it can't find another member;
  // never write that into m.direct or we pollute it with a self-key.
  const guess = room.guessDMUserId()
  if (guess && guess !== me) return guess
  const other = room.getJoinedMembers().find((m) => m.userId !== me)
  return other?.userId ?? null
}

async function setNotDirectRoomIds(client: MatrixClient, ids: Set<string>): Promise<void> {
  await client.setAccountData(NOT_DIRECT_EVENT as keyof sdk.AccountDataEvents, {
    rooms: [...ids],
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  } as any)
}

/**
 * One-time repair: m.direct can accumulate a *self-key* — rooms mapped under your
 * own user id — because Room.guessDMUserId() falls back to you when it can't see
 * the other member, and markRoomAsDirect used to write that. Re-key those rooms to
 * their real partner and drop the self entry, so the data is correct for every
 * client (not just our read-side guard). Idempotent: only writes when it changes
 * something, and leaves genuine self-only rooms (no other member) untouched.
 */
export async function cleanupDirectSelfKeys(client: MatrixClient): Promise<void> {
  const me = client.getUserId()
  if (!me) return
  const content = getDirectContent(client)
  const selfRooms = content[me]
  if (!Array.isArray(selfRooms) || selfRooms.length === 0) return

  const next: DirectContent = { ...content }
  const keepSelf: string[] = []
  let changed = false

  for (const roomId of selfRooms) {
    const partner = client
      .getRoom(roomId)
      ?.getMembers()
      .find((m) => (m.membership === 'join' || m.membership === 'invite') && m.userId !== me)?.userId
    if (!partner) {
      keepSelf.push(roomId) // genuine self-room — don't disturb it
      continue
    }
    const list = next[partner] ?? []
    if (!list.includes(roomId)) next[partner] = [...list, roomId]
    changed = true
  }

  if (!changed) return
  if (keepSelf.length) next[me] = keepSelf
  else delete next[me]
  await client.setAccountData(EventType.Direct, next)
}

/** Mark a room as a direct message (and clear any "not direct" override). */
export async function markRoomAsDirect(client: MatrixClient, room: Room): Promise<void> {
  const userId = otherMemberId(client, room)
  if (!userId) return

  const content = getDirectContent(client)
  const list = content[userId] ?? []
  if (!list.includes(room.roomId)) {
    content[userId] = [...list, room.roomId]
    await client.setAccountData(EventType.Direct, content)
  }

  const notDirect = getNotDirectRoomIds(client)
  if (notDirect.delete(room.roomId)) await setNotDirectRoomIds(client, notDirect)
}

/** Move a room out of Direct: drop it from m.direct and record the override. */
export async function unmarkRoomAsDirect(client: MatrixClient, room: Room): Promise<void> {
  const content = getDirectContent(client)
  let changed = false
  for (const userId of Object.keys(content)) {
    const filtered = content[userId]!.filter((id) => id !== room.roomId)
    if (filtered.length !== content[userId]!.length) {
      content[userId] = filtered
      changed = true
    }
  }
  if (changed) await client.setAccountData(EventType.Direct, content)

  const notDirect = getNotDirectRoomIds(client)
  notDirect.add(room.roomId)
  await setNotDirectRoomIds(client, notDirect)
}

/** Flip a room's direct/group classification. */
export async function toggleRoomDirect(client: MatrixClient, roomId: string): Promise<void> {
  const room = client.getRoom(roomId)
  if (!room) return
  if (isDirectRoom(client, room)) await unmarkRoomAsDirect(client, room)
  else await markRoomAsDirect(client, room)
}

/** Find an existing, still-active DM room with a user, if one exists. */
function findExistingDirectRoom(client: MatrixClient, userId: string): string | null {
  const roomIds = getDirectContent(client)[userId] ?? []
  for (const roomId of roomIds) {
    const room = client.getRoom(roomId)
    if (room && room.getMyMembership() !== 'leave') return roomId
  }
  return null
}

/**
 * Open a direct message with a user, reusing an existing DM room when present
 * and otherwise creating a new encrypted one. Returns the room ID.
 */
export async function startDirectMessage(client: MatrixClient, userId: string): Promise<string> {
  const existing = findExistingDirectRoom(client, userId)
  if (existing) return existing

  const result = await client.createRoom({
    is_direct: true,
    invite: [userId],
    preset: sdk.Preset.TrustedPrivateChat,
    initial_state: [
      {
        type: 'm.room.encryption',
        state_key: '',
        content: { algorithm: 'm.megolm.v1.aes-sha2' },
      },
    ],
  })

  const content = getDirectContent(client)
  const list = content[userId] ?? []
  if (!list.includes(result.room_id)) {
    content[userId] = [...list, result.room_id]
    await client.setAccountData(EventType.Direct, content)
  }
  return result.room_id
}
